import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components/native';
import { FlatList } from 'react-native';
import { P } from './Typography';
import accessibilityTags from '../../accessibilityTags';

// const ITEM_HEIGHT = 38;

const StyledPickerView = styled.View<{ itemHeight: number }>`
  margin-left: 8px;
  margin-right: 8px;
  height: ${p => p.itemHeight * 3}px;
  align-items: center;
`;

const StyledBottomLine = styled.View<{ itemHeight: number }>`
  height: 1.5px;
  width: 100%;
  opacity: 0.5;
  background-color: black;
  position: absolute;
  align-self: center;
  top: ${p => (p.itemHeight * 3) / 2 + p.itemHeight / 2}px;
`;

const StyledUpperLine = styled.View<{ itemHeight: number }>`
  height: 1.5px;
  width: 100%;
  background-color: black;
  position: absolute;
  align-self: center;
  opacity: 0.5;
  bottom: ${p => (p.itemHeight * 3) / 2 + p.itemHeight / 2}px;
`;

const StyledPickerItem = styled.View<{ itemHeight: number }>`
  height: ${p => p.itemHeight}px;
  justify-content: center;
  padding: 0px;
  margin: 0px;
`;

const StyledLabel = styled(P)<{ selected?: boolean; textSize: number }>`
  padding-left: 8px;
  padding-right: 8px;
  height: ${p => p.textSize + 4}px;
  font-size: ${p => p.textSize}px;
  line-height: ${p => p.textSize + 4}px;
  text-align: center;
  color: black;
  opacity: ${p => (p.selected ? 1 : 0.4)};
`;

export interface FlatListPickerProps {
  options: string[];
  onValueChanged: (data: string) => void;
  itemHeight?: number;
  textSize?: number;
  initialValue?: string;
  ellipsizeMode?: 'head' | 'clip' | 'middle' | 'tail';
}

const FlatListPicker: React.FC<FlatListPickerProps> = ({
  options,
  onValueChanged,
  itemHeight = 42,
  textSize = 18,
  initialValue,
  ellipsizeMode
}) => {
  const data = ['', ...options, ''];
  const flatListRef = useRef<FlatList>();
  const [selected, setSelected] = useState(initialValue || options[0]);
  const [prevIndex, setPrevIndex] = useState(0);
  const getOffsets = () => {
    const arr: number[] = [];
    options.forEach((_, index) => {
      arr.push(itemHeight * index);
    });
    return arr;
  };
  useEffect(() => {
    if (options.indexOf(selected) !== prevIndex && options.length > 0) {
      const index =
        options.indexOf(selected) < 0 ? 0 : options.indexOf(selected);
      setSelected(options[index]);
      onValueChanged(options[index]);
      setPrevIndex(index);
      setTimeout(() => {
        flatListRef?.current?.scrollToIndex({ index: index });
      }, 200);
    }
  }, [onValueChanged, options, prevIndex, selected, flatListRef]);
  return (
    <StyledPickerView itemHeight={itemHeight}>
      <StyledUpperLine itemHeight={itemHeight} />
      <StyledBottomLine itemHeight={itemHeight} />
      <FlatList
        ref={flatListRef}
        onScroll={event => {
          const scrollOffset = event.nativeEvent.contentOffset.y;
          const index = Math.ceil(scrollOffset) / itemHeight;
          if (options[index]) {
            if (options[index] !== selected) {
              setSelected(options[index]);
              onValueChanged(options[index]);
              setPrevIndex(index);
            }
          }
        }}
        initialNumToRender={data.length - 1}
        onScrollToIndexFailed={error => {
          flatListRef.current?.scrollToOffset({
            offset: error.averageItemLength * error.index,
            animated: true
          });
          setTimeout(() => {
            if (data.length !== 0 && flatListRef.current !== null) {
              flatListRef.current?.scrollToIndex({
                index: error.index,
                animated: true
              });
            }
          }, 100);
        }}
        onLayout={() => {
          if (flatListRef.current) {
            setTimeout(() => {
              const index = initialValue ? options.indexOf(initialValue) : 0;
              flatListRef?.current?.scrollToIndex({
                index: index < 0 ? 0 : index
              });
            }, 200);
          }
        }}
        overScrollMode='never'
        showsVerticalScrollIndicator={false}
        data={data}
        snapToOffsets={getOffsets()}
        snapToAlignment='start'
        snapToEnd={false}
        renderItem={item => {
          return (
            <StyledPickerItem itemHeight={itemHeight}>
              <StyledLabel
                numberOfLines={Boolean(ellipsizeMode) ? 1 : undefined}
                ellipsizeMode={ellipsizeMode}
                selected={item.item === selected}
                textSize={textSize}
                accessibilityLabel={`${
                  item.item === selected
                    ? `${accessibilityTags.timePickerSelectedID}-${
                        options[0].includes('1')
                          ? 'hours'
                          : options[0] === '00'
                          ? 'minutes'
                          : 'ampm'
                      }`
                    : ''
                }`}
                testID={`${
                  item.item === selected
                    ? `${accessibilityTags.timePickerSelectedID}-${
                        options[0].includes('1')
                          ? 'hours'
                          : options[0] === '00'
                          ? 'minutes'
                          : 'ampm'
                      }`
                    : ''
                }`}
              >
                {item.item}
              </StyledLabel>
            </StyledPickerItem>
          );
        }}
        keyExtractor={(_, index) => index.toString()}
      />
    </StyledPickerView>
  );
};

export default FlatListPicker;
